import { useEffect, useState } from 'react';
import { Download, Printer } from 'lucide-react';
import Button from '../ui/Button';
import { inputClass } from '../fieldWork/FormModal';
import labourService from '../../services/labour';
import fieldWorkService from '../../services/fieldWork';
import { errorMessage } from '../../utils/apiResponse';
import type { LabourProvider } from '../../types/fieldWork';
import type { ContractorStatement } from '../../types/labour';
import { DateRangeInputs, Notice } from './shared';
import { hrs, money, unitsText, useDateRange } from './labourUtils';

/**
 * What a labour provider should be invoicing for a period, from the hours and
 * output recorded here — the thing to hold their invoice against.
 */
export default function StatementsPanel() {
  const range = useDateRange();
  const [providers, setProviders] = useState<LabourProvider[]>([]);
  const [providerId, setProviderId] = useState<number | ''>('');
  const [st, setSt] = useState<ContractorStatement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fieldWorkService.getProviders(true).then(p => {
      setProviders(p);
      if (p.length) setProviderId(p[0].id);
    }).catch(() => setProviders([]));
  }, []);

  useEffect(() => {
    if (!providerId) return;
    let live = true;
    labourService.getContractorStatement(providerId, range.from, range.to)
      .then(r => { if (live) { setSt(r); setError(null); } })
      .catch(e => { if (live) setError(errorMessage(e, 'Could not load the statement')); });
    return () => { live = false; };
  }, [providerId, range.from, range.to]);

  if (!providers.length) {
    return <p className="text-sm text-neutral-500 py-6 text-center">Add a labour provider on the Workforce page first.</p>;
  }
  const cur = st?.currency ?? 'AUD';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 print:hidden">
        <label className="text-xs text-neutral-600">
          <span className="block mb-1">Provider</span>
          <select className={inputClass} value={providerId} onChange={e => setProviderId(Number(e.target.value))}>
            {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <DateRangeInputs {...range} />
        <Button size="sm" variant="outline" disabled={!providerId}
          onClick={() => providerId && labourService.downloadContractorStatementCsv(providerId, range.from, range.to).catch(e => setError(errorMessage(e, 'Download failed')))}>
          <Download className="h-4 w-4" /> CSV
        </Button>
        <Button size="sm" variant="outline" onClick={() => window.print()}>
          <Printer className="h-4 w-4" /> Print
        </Button>
      </div>
      {error && <Notice tone="error">{error}</Notice>}
      {st && (
        <>
          <div>
            <h3 className="font-semibold text-neutral-900">{st.provider.name}</h3>
            <p className="text-sm text-neutral-500">
              {st.from} to {st.to}{st.provider.business_number ? ` · ${st.provider.business_number}` : ''}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div className="rounded-lg border border-neutral-200 p-3"><p className="text-xs text-neutral-500">Hours</p><p className="text-lg font-semibold">{hrs(st.totals.hours)}</p></div>
            <div className="rounded-lg border border-neutral-200 p-3"><p className="text-xs text-neutral-500">Should invoice (inc {st.tax_name})</p><p className="text-lg font-semibold">{money((st.totals.charge ?? 0) + (st.totals.tax ?? 0), cur)}</p></div>
            <div className="rounded-lg border border-neutral-200 p-3"><p className="text-xs text-neutral-500">People</p><p className="text-lg font-semibold">{st.totals.worker_count ?? 0}</p></div>
          </div>
          {(st.totals.unrated_hours ?? 0) > 0 && (
            <Notice tone="warn">{hrs(st.totals.unrated_hours)} have no rate for this provider, so they are not in the total.</Notice>
          )}
          {st.rows.length === 0 ? (
            <p className="text-sm text-neutral-500 py-6 text-center">No time from this provider in the range.</p>
          ) : (
            <div className="overflow-x-auto border border-neutral-200 rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Date</th>
                    <th className="text-left px-3 py-2 font-medium">Person</th>
                    <th className="text-left px-3 py-2 font-medium">Work</th>
                    <th className="text-left px-3 py-2 font-medium">Block</th>
                    <th className="text-right px-3 py-2 font-medium">Hours</th>
                    <th className="text-left px-3 py-2 font-medium">Output</th>
                    <th className="text-right px-3 py-2 font-medium">Ex {st.tax_name}</th>
                    <th className="text-right px-3 py-2 font-medium">Inc {st.tax_name}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {st.rows.map((r, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 whitespace-nowrap">{String(r.date)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{String(r.worker_name ?? '')}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{String(r.activity_type_name ?? '—')}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{String(r.block_name ?? '—')}</td>
                      <td className="px-3 py-2 text-right">{hrs(r.hours)}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-neutral-600">{unitsText(r.units)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{money(r.charge, cur)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{money((r.charge ?? 0) + (r.tax ?? 0), cur)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
